import type { GridPoint, LevelConfig, NodeConfig } from "@gridflow/core";
import { createLevelState } from "@gridflow/core";

export type LevelCoordinate = readonly [x: number, y: number];

export interface LevelJsonNode {
  readonly color: string;
  readonly role: "origin" | "target";
  readonly x: number;
  readonly y: number;
  readonly targetLength?: number;
}

export interface LevelJson {
  readonly id: string;
  readonly order: number;
  readonly width: number;
  readonly height: number;
  readonly fillRequired: boolean;
  readonly teachingGoal: string;
  readonly obstacles?: readonly LevelCoordinate[];
  readonly nodes: readonly LevelJsonNode[];
  readonly solution: Readonly<Record<string, readonly LevelCoordinate[]>>;
}

export interface LevelValidationIssue {
  readonly code: string;
  readonly message: string;
}

export const LEVEL_JSON_SCHEMA = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  title: "GridFlowLevel",
  type: "object",
  required: ["id", "order", "width", "height", "fillRequired", "teachingGoal", "nodes", "solution"],
  additionalProperties: false,
  properties: {
    id: { type: "string", minLength: 1 },
    order: { type: "integer", minimum: 1 },
    width: { type: "integer", minimum: 1, maximum: 6 },
    height: { type: "integer", minimum: 1, maximum: 6 },
    fillRequired: { type: "boolean" },
    teachingGoal: { type: "string", minLength: 1 },
    obstacles: {
      type: "array",
      items: { $ref: "#/$defs/coordinate" }
    },
    nodes: {
      type: "array",
      minItems: 2,
      items: { $ref: "#/$defs/node" }
    },
    solution: {
      type: "object",
      additionalProperties: {
        type: "array",
        minItems: 1,
        items: { $ref: "#/$defs/coordinate" }
      }
    }
  },
  $defs: {
    coordinate: {
      type: "array",
      prefixItems: [
        { type: "integer", minimum: 0 },
        { type: "integer", minimum: 0 }
      ],
      minItems: 2,
      maxItems: 2
    },
    node: {
      type: "object",
      required: ["color", "role", "x", "y"],
      additionalProperties: false,
      properties: {
        color: { type: "string", minLength: 1 },
        role: { enum: ["origin", "target"] },
        x: { type: "integer", minimum: 0 },
        y: { type: "integer", minimum: 0 },
        targetLength: { type: "integer", minimum: 1 }
      }
    }
  }
} as const;

export function parseLevelJson(level: LevelJson): LevelConfig {
  const issues = validateLevelJsonShape(level);
  if (issues.length > 0) {
    throw new Error(issues.map((issue) => `${issue.code}: ${issue.message}`).join("\n"));
  }

  const configBase = {
    id: level.id,
    width: level.width,
    height: level.height,
    fillRequired: level.fillRequired,
    nodes: level.nodes.map(jsonNodeToConfig)
  };
  const config: LevelConfig =
    level.obstacles !== undefined
      ? {
          ...configBase,
          obstacles: level.obstacles.map(coordinateToPoint)
        }
      : configBase;

  createLevelState(config);
  return config;
}

export function validateLevelJsonShape(level: LevelJson): readonly LevelValidationIssue[] {
  const issues: LevelValidationIssue[] = [];

  if (level.id.trim().length === 0) {
    issues.push({ code: "EMPTY_ID", message: "Level id cannot be empty." });
  }
  if (!Number.isInteger(level.order) || level.order <= 0) {
    issues.push({
      code: "INVALID_ORDER",
      message: `Level ${level.id} order must be a positive integer.`
    });
  }
  if (!Number.isInteger(level.width) || level.width < 1 || level.width > 6) {
    issues.push({ code: "INVALID_WIDTH", message: `Level ${level.id} width must be 1..6.` });
  }
  if (!Number.isInteger(level.height) || level.height < 1 || level.height > 6) {
    issues.push({ code: "INVALID_HEIGHT", message: `Level ${level.id} height must be 1..6.` });
  }
  if (level.teachingGoal.trim().length === 0) {
    issues.push({
      code: "EMPTY_TEACHING_GOAL",
      message: `Level ${level.id} needs a teaching goal.`
    });
  }

  const coordinates = [...(level.obstacles ?? []), ...Object.values(level.solution).flat()];
  for (const coordinate of coordinates) {
    if (!isCoordinate(coordinate)) {
      issues.push({
        code: "INVALID_COORDINATE",
        message: `Level ${level.id} has invalid coordinate.`
      });
      continue;
    }
    if (coordinate[0] >= level.width || coordinate[1] >= level.height) {
      issues.push({
        code: "COORDINATE_OUT_OF_BOUNDS",
        message: `Level ${level.id} coordinate ${coordinate.join(",")} is outside the board.`
      });
    }
  }

  for (const node of level.nodes) {
    if (node.color.trim().length === 0) {
      issues.push({
        code: "EMPTY_NODE_COLOR",
        message: `Level ${level.id} has an empty node color.`
      });
    }
    if (
      node.role === "origin" &&
      (!Number.isInteger(node.targetLength) || (node.targetLength ?? 0) <= 0)
    ) {
      issues.push({
        code: "INVALID_TARGET_LENGTH",
        message: `Level ${level.id} origin ${node.color} needs a positive targetLength.`
      });
    }
    if (node.role === "target" && node.targetLength !== undefined) {
      issues.push({
        code: "TARGET_HAS_LENGTH",
        message: `Level ${level.id} target ${node.color} must not carry targetLength.`
      });
    }
  }

  return issues;
}

export function coordinateToPoint(coordinate: LevelCoordinate): GridPoint {
  return { x: coordinate[0], y: coordinate[1] };
}

export function pointToCoordinate(point: GridPoint): LevelCoordinate {
  return [point.x, point.y];
}

function jsonNodeToConfig(node: LevelJsonNode): NodeConfig {
  if (node.role === "origin") {
    return {
      color: node.color,
      role: "origin",
      position: { x: node.x, y: node.y },
      targetLength: node.targetLength ?? 0
    };
  }

  return {
    color: node.color,
    role: "target",
    position: { x: node.x, y: node.y }
  };
}

function isCoordinate(value: unknown): value is LevelCoordinate {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    Number.isInteger(value[0]) &&
    Number.isInteger(value[1]) &&
    value[0] >= 0 &&
    value[1] >= 0
  );
}
